import { c as _c } from "react/compiler-runtime";
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BetterTooltip } from '@/components/ui/tooltip';
type BaseSearchIssuesProps<IssueType> = {
  isOpen: boolean;
  onOpenChangeAction: (open: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  debouncedSearch: string;
  searchResults: IssueType[];
  isLoading: boolean;
  error: string | null;
  onSelectAction: (issue: IssueType) => void;
  getIssueKey: (issue: IssueType) => string;
  getIssueSummary: (issue: IssueType) => string;
  tooltipContent: (issue: IssueType) => string | React.ReactElement;
  popoverWidthClass?: string;
  showIssueKey?: boolean;
};
export function BaseSearchIssues(t0) {
  const $ = _c(42);
  const {
    isOpen,
    onOpenChangeAction,
    search,
    setSearch,
    debouncedSearch,
    searchResults,
    isLoading,
    error,
    onSelectAction,
    getIssueKey,
    getIssueSummary,
    tooltipContent,
    popoverWidthClass: t1,
    showIssueKey: t2
  } = t0;
  const popoverWidthClass = t1 === undefined ? "w-80" : t1;
  const showIssueKey = t2 === undefined ? true : t2;
  const isDebouncing = search !== debouncedSearch;
  let t3;
  let t4;
  if ($[0] !== isOpen || $[1] !== setSearch) {
    t3 = () => {
      if (!isOpen) {
        setSearch("");
      }
    };
    t4 = [isOpen, setSearch];
    $[0] = isOpen;
    $[1] = setSearch;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <BetterTooltip content="Link issue"><PopoverTrigger asChild={true}><Button variant="ghost" size="icon" className="size-6 text-zinc-500 hover:text-zinc-300" aria-label="Search issues"><Plus className="size-3.5" /></Button></PopoverTrigger></BetterTooltip>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = `${popoverWidthClass} p-0`;
  let t7;
  if ($[5] !== search || $[6] !== setSearch) {
    t7 = <CommandInput placeholder="Search issues..." value={search} onValueChange={setSearch} className="h-9" autoFocus={true} />;
    $[5] = search;
    $[6] = setSearch;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  bb0: {
    const hasSearch = search.length > 0 || debouncedSearch.length > 0;
    if (!hasSearch) {
      let t9;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <div className="text-sm text-zinc-400 px-3 py-6 text-center">Type to search issues...</div>;
        $[8] = t9;
      } else {
        t9 = $[8];
      }
      t8 = t9;
      break bb0;
    }
    if (isLoading || isDebouncing) {
      let t9;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <div className="text-sm text-zinc-400 px-3 py-6 text-center">Searching...</div>;
        $[9] = t9;
      } else {
        t9 = $[9];
      }
      t8 = t9;
      break bb0;
    }
    if (error) {
      let t9;
      if ($[10] !== error) {
        t9 = <div className="text-sm text-zinc-400 px-3 py-6 text-center">{error}</div>;
        $[10] = error;
        $[11] = t9;
      } else {
        t9 = $[11];
      }
      t8 = t9;
      break bb0;
    }
    if (searchResults.length > 0) {
      let t9;
      if ($[12] !== getIssueKey || $[13] !== getIssueSummary || $[14] !== onOpenChangeAction || $[15] !== onSelectAction || $[16] !== searchResults || $[17] !== showIssueKey || $[18] !== tooltipContent) {
        let t10;
        if ($[20] !== getIssueKey || $[21] !== getIssueSummary || $[22] !== onOpenChangeAction || $[23] !== onSelectAction || $[24] !== showIssueKey || $[25] !== tooltipContent) {
          t10 = issue => <CommandItem key={getIssueKey(issue)} value={getIssueKey(issue)} onSelect={() => {
            onSelectAction(issue);
            onOpenChangeAction(false);
          }} className="text-sm">{showIssueKey && <span className="text-sm mr-1 text-zinc-400 shrink-0 w-16 text-left truncate">{getIssueKey(issue)}</span>}<BetterTooltip content={tooltipContent(issue) || ""} side="left"><span className="text-sm truncate">{getIssueSummary(issue)}</span></BetterTooltip></CommandItem>;
          $[20] = getIssueKey;
          $[21] = getIssueSummary;
          $[22] = onOpenChangeAction;
          $[23] = onSelectAction;
          $[24] = showIssueKey;
          $[25] = tooltipContent;
          $[26] = t10;
        } else {
          t10 = $[26];
        }
        t9 = searchResults.map(t10);
        $[12] = getIssueKey;
        $[13] = getIssueSummary;
        $[14] = onOpenChangeAction;
        $[15] = onSelectAction;
        $[16] = searchResults;
        $[17] = showIssueKey;
        $[18] = tooltipContent;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      let t10;
      if ($[27] !== t9) {
        t10 = <CommandGroup>{t9}</CommandGroup>;
        $[27] = t9;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      t8 = t10;
      break bb0;
    }
    let t9;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <CommandEmpty className="py-6 text-center text-sm">No issues found</CommandEmpty>;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    t8 = t9;
  }
  let t9;
  if ($[30] !== t8) {
    t9 = <CommandList className="text-sm">{t8}</CommandList>;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t7 || $[33] !== t9) {
    t10 = <Command shouldFilter={false}>{t7}{t9}</Command>;
    $[32] = t7;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t6) {
    t11 = <PopoverContent align="center" side="left" className={t6}>{t10}</PopoverContent>;
    $[35] = t10;
    $[36] = t6;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== isOpen || $[39] !== onOpenChangeAction || $[40] !== t11) {
    t12 = <Popover open={isOpen} onOpenChange={onOpenChangeAction}>{t5}{t11}</Popover>;
    $[38] = isOpen;
    $[39] = onOpenChangeAction;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
}